import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CentreList } from './CentreList';
import { CityList } from './CityList';
import { DispensaryList } from './DispensaryList';
import { ReferenceImportScreen } from './ReferenceImportScreen';
import { PageActions } from '../../app/shell/PageActions';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import styles from './ReferenceScreen.module.css';

/**
 * The reference hierarchy screen — dispensary -> city -> centre, and the
 * centre CSV import. `/dispensaries` (`navigation.ts`'s own pre-existing,
 * ungated entry — one of the nine Phase 1 shell items that pointed nowhere
 * until this task wired it up; see that module's own docblock).
 *
 * READING NEEDS NO PERMISSION — the API's own routes carry only
 * `auth:sanctum`/`active` on every `GET` (`routes/api.php`'s own docblock:
 * "any active, authenticated account"). Every level therefore renders for
 * whoever reaches this path, and `permissions` travels down only so each
 * level can decide what to render BESIDES the table — `reference.manage`
 * gates every create/edit/activate/deactivate/delete control, the identical
 * shape `UserListScreen.tsx` already established for `user.*`.
 *
 * The Import tab is gated ENTIRELY on `reference.manage` — `POST
 * /reference/centres/import` needs it exactly as every other write route
 * does, so a caller without it would find every control inside inert. Hiding
 * the tab rather than rendering an empty shell mirrors `App.tsx`'s own
 * `auditLog` nav entry, which is the only OTHER entry in this application
 * gated in the nav itself rather than only in what renders.
 *
 * One `SegmentedControl`, not four permanently-mounted screens: each level's
 * own list effect fetches on mount, and keeping all four mounted at once
 * would fire every request the instant this screen opens, for three tables
 * an administrator may never look at in this visit.
 */

export interface ReferenceScreenProps {
  permissions: readonly string[];
}

type Tab = 'dispensaries' | 'cities' | 'centres' | 'import';

export function ReferenceScreen({ permissions }: ReferenceScreenProps) {
  const { t } = useTranslation();
  const canManage = permissions.includes('reference.manage');

  const [tab, setTab] = useState<Tab>('dispensaries');

  const options = [
    { value: 'dispensaries', label: t('reference.tabDispensaries') },
    { value: 'cities', label: t('reference.tabCities') },
    { value: 'centres', label: t('reference.tabCentres') },
    ...(canManage ? [{ value: 'import', label: t('reference.tabImport') }] : []),
  ];

  // A caller who lost `reference.manage` between renders (a role change
  // elsewhere in this session) must not stay parked on a tab that no longer
  // exists — the same reason `App.tsx`'s own `titlePath` falls back rather
  // than trusting stale state.
  const activeTab: Tab = tab === 'import' && !canManage ? 'dispensaries' : tab;

  return (
    <div className={styles['page']}>
      {/* No "Reference data" sub-title: the shell's <h1> already names the
          page. The level switch rides in the title row (PageActions), before
          the active tab's own Add command, which each list portals there too. */}
      <PageActions>
        <SegmentedControl
          label={t('reference.tabsLabel')}
          labelPlacement="inline"
          options={options}
          value={activeTab}
          onChange={(value) => {
            setTab(value as Tab);
          }}
        />
      </PageActions>

      {activeTab === 'dispensaries' ? <DispensaryList permissions={permissions} /> : null}
      {activeTab === 'cities' ? <CityList permissions={permissions} /> : null}
      {activeTab === 'centres' ? <CentreList permissions={permissions} /> : null}
      {activeTab === 'import' ? <ReferenceImportScreen permissions={permissions} /> : null}
    </div>
  );
}
